import { z } from 'zod';
import { PhoneSchema } from './auth';
import { CountryCodeSchema } from './geo';
import { VehicleClassSchema } from './riders';

/* ------------------------------------------------------------------ */
/* Dispatch + matching (W13)                                           */
/*                                                                     */
/* A long-running worker matches each zone every few seconds: orders   */
/* that need a rider (search timed to food-ready) × online riders      */
/* (radius, vehicle, cash, cool-off, stacking) → score → greedy        */
/* one-offer-per-rider. Offers ring one rider at a time for 30 s. A    */
/* no-rider ladder adds a bonus, widens the search, alerts Ops and     */
/* finally cancels with a refund + credit. Restaurant own riders get   */
/* first offer for their outlet's orders.                              */
/*                                                                     */
/* Money is always in minor units (paise / paisa).                     */
/* ------------------------------------------------------------------ */

const Version = z.number().int().min(1);
const Reason = z.string().trim().min(3, 'Say why (at least 3 characters)').max(300);
const Weight = z.number().min(0).max(1);

/* ---------------- jobs + ladder ---------------- */

/**
 * queued      waiting for its search time (food-ready minus rider approach)
 * searching   rounds are offering it to riders
 * assigned    a rider accepted (the trip carries on in the rider flow)
 * closed      delivered, cancelled, picked up, or taken over by Ops (manual)
 */
export const DISPATCH_JOB_STATUSES = ['queued', 'searching', 'assigned', 'closed'] as const;
export const DispatchJobStatusSchema = z.enum(DISPATCH_JOB_STATUSES);
export type DispatchJobStatus = z.infer<typeof DispatchJobStatusSchema>;

export const DISPATCH_CLOSE_REASONS = [
  'delivered',
  'cancelled',
  'no_rider',
  'manual',
  'picked_up',
] as const;
export const DispatchCloseReasonSchema = z.enum(DISPATCH_CLOSE_REASONS);
export type DispatchCloseReason = z.infer<typeof DispatchCloseReasonSchema>;

/** The no-rider ladder, in order. 0 = nothing happened yet. */
export const LADDER_STEPS = ['none', 'bonus', 'widened', 'ops_alert', 'cancelled'] as const;
export const LadderStepSchema = z.enum(LADDER_STEPS);
export type LadderStep = z.infer<typeof LadderStepSchema>;
export const LADDER_STEP_LABELS: Record<LadderStep, string> = {
  none: 'Searching',
  bonus: 'Bonus added',
  widened: 'Search widened',
  ops_alert: 'Ops alerted',
  cancelled: 'Cancelled — no rider',
};

/** Board colour for a row: ok, amber (rider late / slow search), red (ops alert / no rider). */
export const ATTENTION_LEVELS = ['ok', 'amber', 'red'] as const;
export const AttentionLevelSchema = z.enum(ATTENTION_LEVELS);
export type AttentionLevel = z.infer<typeof AttentionLevelSchema>;

export const RIDER_LATE_STATES = ['on_time', 'warn', 'reassign'] as const;
export const RiderLateStateSchema = z.enum(RIDER_LATE_STATES);
export type RiderLateState = z.infer<typeof RiderLateStateSchema>;

/* ---------------- weights ---------------- */

/** Score weights (sum to 1) + the idle cap used by the idle part. */
export const DispatchWeightsSchema = z
  .object({
    distance: Weight,
    idle: Weight,
    fairness: Weight,
    rating: Weight,
    stack: Weight,
    fleet: Weight,
    idleCapMinutes: z.number().int().min(1).max(240),
  })
  .refine(
    (w) => Math.abs(w.distance + w.idle + w.fairness + w.rating + w.stack + w.fleet - 1) <= 0.001,
    { message: 'The weights must add up to 1', path: ['distance'] },
  );
export type DispatchWeights = z.infer<typeof DispatchWeightsSchema>;

export const DEFAULT_DISPATCH_WEIGHTS: DispatchWeights = {
  distance: 0.4,
  idle: 0.15,
  fairness: 0.15,
  rating: 0.1,
  stack: 0.1,
  fleet: 0.1,
  idleCapMinutes: 30,
};

export const WEIGHT_SOURCES = ['default', 'country', 'zone'] as const;
export const WeightSourceSchema = z.enum(WEIGHT_SOURCES);
export type WeightSource = z.infer<typeof WeightSourceSchema>;

export const WeightsQuerySchema = z.object({
  country: CountryCodeSchema,
  zoneId: z.uuid().optional(),
});
export type WeightsQuery = z.infer<typeof WeightsQuerySchema>;

export const DispatchWeightsViewSchema = z.object({
  country: CountryCodeSchema,
  zoneId: z.uuid().nullable(),
  /** Where the effective weights came from (a zone override, the country row or the defaults). */
  source: WeightSourceSchema,
  weights: z.object({
    distance: z.number(),
    idle: z.number(),
    fairness: z.number(),
    rating: z.number(),
    stack: z.number(),
    fleet: z.number(),
    idleCapMinutes: z.number().int(),
  }),
  /** The country row (for the zone editor's "inherits from"). */
  countryWeights: z
    .object({
      distance: z.number(),
      idle: z.number(),
      fairness: z.number(),
      rating: z.number(),
      stack: z.number(),
      fleet: z.number(),
      idleCapMinutes: z.number().int(),
    })
    .nullable(),
  version: z.number().int().nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
export type DispatchWeightsView = z.infer<typeof DispatchWeightsViewSchema>;

export const PutWeightsInputSchema = z.object({
  country: CountryCodeSchema,
  /** Null/absent = the country row; a zone id = that zone's override. */
  zoneId: z.uuid().nullable().optional(),
  weights: DispatchWeightsSchema,
  /** Null when the row does not exist yet. */
  expectedVersion: Version.nullable().optional(),
});
export type PutWeightsInput = z.infer<typeof PutWeightsInputSchema>;

export const DeleteZoneWeightsInputSchema = z.object({
  country: CountryCodeSchema,
  zoneId: z.uuid(),
});
export type DeleteZoneWeightsInput = z.infer<typeof DeleteZoneWeightsInputSchema>;

/* ---------------- score breakdown ---------------- */

/** Each part is already multiplied by its weight; the score is their sum. */
export const ScorePartsSchema = z.object({
  distance: z.number(),
  idle: z.number(),
  fairness: z.number(),
  rating: z.number(),
  stack: z.number(),
  fleet: z.number(),
  approachKm: z.number().nullable(),
  idleMinutes: z.number().nullable(),
});
export type ScoreParts = z.infer<typeof ScorePartsSchema>;

/* ---------------- board ---------------- */

export const DispatchBoardQuerySchema = z.object({
  townId: z.uuid().optional(),
  zoneId: z.uuid().optional(),
});
export type DispatchBoardQuery = z.infer<typeof DispatchBoardQuerySchema>;

export const CurrentOfferSchema = z.object({
  tripId: z.uuid(),
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  offeredAt: z.string(),
  expiresAt: z.string(),
  secondsLeft: z.number().int(),
  score: z.number().nullable(),
  ownFleet: z.boolean(),
  stacked: z.boolean(),
  bonusMinor: z.number().int(),
});
export type CurrentOffer = z.infer<typeof CurrentOfferSchema>;

export const AssignedRiderSchema = z.object({
  tripId: z.uuid(),
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  tripStatus: z.string(),
  acceptedAt: z.string().nullable(),
  plannedArrivalAt: z.string().nullable(),
  late: RiderLateStateSchema,
  lateByMinutes: z.number().int(),
});
export type AssignedRider = z.infer<typeof AssignedRiderSchema>;

export const DispatchBoardOrderSchema = z.object({
  jobId: z.uuid(),
  orderId: z.uuid(),
  orderNumber: z.string(),
  country: CountryCodeSchema,
  townId: z.uuid(),
  zoneId: z.uuid().nullable(),
  outletId: z.uuid(),
  restaurantName: z.string(),
  outletName: z.string(),
  status: DispatchJobStatusSchema,
  orderStatus: z.string(),
  readyBy: z.string().nullable(),
  readyAt: z.string().nullable(),
  /** Seconds until the food is due ready (negative when late); null when unknown. */
  readyInSeconds: z.number().int().nullable(),
  searchAt: z.string(),
  searchStartedAt: z.string().nullable(),
  /** Seconds since the search started (0 before). */
  searchSeconds: z.number().int(),
  ladderStep: LadderStepSchema,
  ladderPaused: z.boolean(),
  priority: z.boolean(),
  radiusKm: z.number(),
  bonusMinor: z.number().int(),
  codMinor: z.number().int().nullable(),
  currentOffer: CurrentOfferSchema.nullable(),
  assigned: AssignedRiderSchema.nullable(),
  offers: z.number().int(),
  /** Riders the last round could have offered this order to. */
  candidates: z.number().int(),
  /** Food ready for longer than dispatch.food_wait_remake_minutes without a pickup. */
  foodWaiting: z.boolean(),
  attention: AttentionLevelSchema,
  version: z.number().int(),
});
export type DispatchBoardOrder = z.infer<typeof DispatchBoardOrderSchema>;

export const BOARD_RIDER_STATES = ['idle', 'offered', 'on_trip', 'cooloff'] as const;
export const BoardRiderStateSchema = z.enum(BOARD_RIDER_STATES);
export type BoardRiderState = z.infer<typeof BoardRiderStateSchema>;

export const DispatchBoardRiderSchema = z.object({
  riderId: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  vehicleClass: VehicleClassSchema,
  state: BoardRiderStateSchema,
  openTrips: z.number().int(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  cashHeldMinor: z.number().int(),
  cooloffUntil: z.string().nullable(),
  idleMinutes: z.number().int().nullable(),
  /** Outlets this rider is an own rider for (empty for gig riders). */
  ownOutletIds: z.array(z.uuid()),
});
export type DispatchBoardRider = z.infer<typeof DispatchBoardRiderSchema>;

export const WorkerHealthSchema = z.object({
  /** True when the worker beat within the last 3 rounds. */
  running: z.boolean(),
  lastBeatAt: z.string().nullable(),
  lastRoundAt: z.string().nullable(),
  secondsSinceLastRound: z.number().int().nullable(),
});
export type WorkerHealth = z.infer<typeof WorkerHealthSchema>;

export const DispatchBoardSchema = z.object({
  at: z.string(),
  counts: z.object({
    searching: z.number().int(),
    queued: z.number().int(),
    offersOut: z.number().int(),
    /** Searching for at least dispatch.no_rider_ops_alert_after_minutes. */
    noRiderAlert: z.number().int(),
    ridersOnline: z.number().int(),
    ridersIdle: z.number().int(),
    ridersLate: z.number().int(),
  }),
  orders: z.array(DispatchBoardOrderSchema),
  riders: z.array(DispatchBoardRiderSchema),
  worker: WorkerHealthSchema,
});
export type DispatchBoard = z.infer<typeof DispatchBoardSchema>;

/* ---------------- order detail ---------------- */

export const OfferHistoryRowSchema = z.object({
  tripId: z.uuid(),
  riderId: z.uuid(),
  riderName: z.string().nullable(),
  status: z.string(),
  offeredAt: z.string(),
  endedAt: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  declineReason: z.string().nullable(),
  cancelNote: z.string().nullable(),
  offeredBy: z.string(),
  score: z.number().nullable(),
  scoreParts: ScorePartsSchema.nullable(),
  ownFleet: z.boolean(),
  stacked: z.boolean(),
  bonusMinor: z.number().int(),
});
export type OfferHistoryRow = z.infer<typeof OfferHistoryRowSchema>;

export const DispatchEventSchema = z.object({
  action: z.string(),
  at: z.string(),
  actor: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()),
});
export type DispatchEvent = z.infer<typeof DispatchEventSchema>;

export const DispatchOrderDetailSchema = z.object({
  job: DispatchBoardOrderSchema,
  offers: z.array(OfferHistoryRowSchema),
  events: z.array(DispatchEventSchema),
});
export type DispatchOrderDetail = z.infer<typeof DispatchOrderDetailSchema>;

/* ---------------- Ops actions ---------------- */

export const DispatchActionInputSchema = z.object({
  /** Optional optimistic check against the job version shown on the board. */
  expectedVersion: Version.optional(),
});
export type DispatchActionInput = z.infer<typeof DispatchActionInputSchema>;

export const DispatchCancelInputSchema = z.object({
  reason: Reason,
});
export type DispatchCancelInput = z.infer<typeof DispatchCancelInputSchema>;

/* ---------------- rounds + simulate ---------------- */

export const RoundsQuerySchema = z.object({
  townId: z.uuid().optional(),
  zoneId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export type RoundsQuery = z.infer<typeof RoundsQuerySchema>;

export const DispatchRoundRowSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  zoneId: z.uuid().nullable(),
  startedAt: z.string(),
  durationMs: z.number().int(),
  orders: z.number().int(),
  candidates: z.number().int(),
  offers: z.number().int(),
  simulated: z.boolean(),
});
export type DispatchRoundRow = z.infer<typeof DispatchRoundRowSchema>;

export const DispatchRoundListSchema = z.object({ rows: z.array(DispatchRoundRowSchema) });
export type DispatchRoundList = z.infer<typeof DispatchRoundListSchema>;

export const SimulateInputSchema = z.object({
  townId: z.uuid(),
  zoneId: z.uuid().nullable().optional(),
  /** Try weights without saving them (the live preview on the weights screen). */
  weights: DispatchWeightsSchema.optional(),
});
export type SimulateInput = z.infer<typeof SimulateInputSchema>;

export const SimulationCellSchema = z.object({
  orderId: z.uuid(),
  riderId: z.uuid(),
  eligible: z.boolean(),
  /** Why the rider cannot take the order (when not eligible). */
  reason: z.string().nullable(),
  score: z.number().nullable(),
  parts: ScorePartsSchema.nullable(),
});
export type SimulationCell = z.infer<typeof SimulationCellSchema>;

export const SimulationResultSchema = z.object({
  townId: z.uuid(),
  zoneId: z.uuid().nullable(),
  orders: z.array(z.object({ orderId: z.uuid(), orderNumber: z.string() })),
  riders: z.array(z.object({ riderId: z.uuid(), name: z.string().nullable() })),
  matrix: z.array(SimulationCellSchema),
  assignments: z.array(z.object({ orderId: z.uuid(), riderId: z.uuid(), score: z.number() })),
  weights: z.object({
    distance: z.number(),
    idle: z.number(),
    fairness: z.number(),
    rating: z.number(),
    stack: z.number(),
    fleet: z.number(),
    idleCapMinutes: z.number().int(),
  }),
});
export type SimulationResult = z.infer<typeof SimulationResultSchema>;

/* ---------------- own riders ---------------- */

export const OwnRiderSchema = z.object({
  linkId: z.uuid(),
  riderId: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  riderStatus: z.string(),
  online: z.boolean(),
  vehicleClass: VehicleClassSchema.nullable(),
  linkedAt: z.string(),
  linkedBy: z.string(),
});
export type OwnRider = z.infer<typeof OwnRiderSchema>;

export const OutletRidersSchema = z.object({
  outletId: z.uuid(),
  outletName: z.string(),
  riders: z.array(OwnRiderSchema),
});
export type OutletRiders = z.infer<typeof OutletRidersSchema>;

export const LinkOwnRiderInputSchema = z.object({
  /** The rider's sign-in phone (the rider must already have started the Rider app). */
  phone: PhoneSchema,
});
export type LinkOwnRiderInput = z.infer<typeof LinkOwnRiderInputSchema>;

export const PartnerOwnRidersSchema = z.object({ outlets: z.array(OutletRidersSchema) });
export type PartnerOwnRiders = z.infer<typeof PartnerOwnRidersSchema>;

export const PartnerLinkRiderInputSchema = z.object({
  outletId: z.uuid(),
  phone: PhoneSchema,
});
export type PartnerLinkRiderInput = z.infer<typeof PartnerLinkRiderInputSchema>;

/* ---------------- worker ---------------- */

export const DispatchTickResultSchema = z.object({
  offersExpired: z.number().int(),
  ladderSteps: z.number().int(),
  cancelled: z.number().int(),
  lateReassigned: z.number().int(),
  enqueued: z.number().int(),
  closed: z.number().int(),
});
export type DispatchTickResult = z.infer<typeof DispatchTickResultSchema>;

export const DispatchRoundResultSchema = z.object({
  townId: z.uuid(),
  zoneId: z.uuid().nullable(),
  orders: z.number().int(),
  candidates: z.number().int(),
  offers: z.number().int(),
  durationMs: z.number().int(),
});
export type DispatchRoundResult = z.infer<typeof DispatchRoundResultSchema>;
